import { PaginationState, SortingState } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fields } from "./Fields";
import { TableView } from "@components/common/TableView";
import {
  createUser,
  getConnectedUser,
  getUsers,
  listEditableRoles,
} from "@services/user";
import {
  FiltersRequest,
  FiltersRequestSchema,
  ListResponse,
  Role,
  User,
} from "@gen/user/v1/user_pb";
import { create } from "@bufbuild/protobuf";
import { CreationForm } from "@components/common/CreationForm";
import { Spinner } from "@thetreep/ui";
import { toSort } from "@services/common";
import { Caisse } from "@gen/caisse/v1/caisse_pb";
import { Agency } from "@gen/agency/v1/agency_pb";
import { getCaisses } from "@services/caisse";
import { getAgencies } from "@services/agency";
import { useNavigate } from "react-router";
import { useToaster } from "@hooks/useToaster";
import { ConnectError } from "@connectrpc/connect";

export function UserManagement() {
  const [isCreationOpen, setIsCreationOpen] = useState(false);
  const [users, setUsers] = useState<ListResponse>();
  const [user, setUser] = useState<User>();
  const [caisses, setCaisses] = useState<Caisse[]>();
  const [agencies, setAgencies] = useState<Agency[]>();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState<FiltersRequest>(
    create(FiltersRequestSchema),
  );
  const [tableKey, setTableKey] = useState(0);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addToast } = useToaster();
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  async function fetchUsers() {
    setIsLoadingUsers(true);
    try {
      const users = await getUsers(
        {
          page: pagination.pageIndex + 1,
          size: pagination.pageSize,
        },
        toSort(sorting),
        filters,
      );
      setUsers(users);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsLoadingUsers(false);
    }
  }

  async function handleSubmit(value: User) {
    try {
      await createUser(value);
      setIsCreationOpen(false);
    } catch (e: unknown) {
      if (e instanceof ConnectError && e.rawMessage) {
        addToast("error", e.rawMessage);
      } else {
        addToast("error", t("genericError"));
      }
    }
  }

  function getAddUserLabel() {
    if (!user) {
      return "";
    }
    switch (user.role) {
      case Role.ADMIN:
      case Role.TRAVEL_AGENT:
        return t("admin.user.addTravelAgentOrCaisseAssociate");
      case Role.CAISSE_ASSOCIATE:
        return t("admin.user.addCaisseAssociate");
      default:
        return "";
    }
  }

  function handleFilters(next: FiltersRequest) {
    setFilters(next);
    if (pagination.pageIndex !== 0) {
      setPagination({ ...pagination, pageIndex: 0 });
      setTableKey((k) => k + 1);
    }
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchUsers();
    }, 300);
    return () => clearTimeout(timeout);
  }, [pagination, sorting, filters, isCreationOpen]);

  useEffect(() => {
    (async () => {
      try {
        const user = await getConnectedUser();
        if (user != null) {
          setUser(user);
        }
        setCaisses((await getCaisses(1, 100)).caisses);
        setAgencies((await getAgencies(1, 100)).agencies);
      } catch (error) {
        console.error("Error fetching user, caisses, or agencies:", error);
      }
    })();
  }, []);

  if (!user || !agencies || !caisses) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <TableView
        isLoadingItems={isLoadingUsers}
        fields={fields(t, agencies, caisses)}
        items={users?.users}
        title={t("admin.user.userManagement")}
        pagination={pagination}
        sorting={sorting}
        filters={filters}
        tableKey={tableKey}
        totalItems={Number(users?.page?.totalElements)}
        setPagination={setPagination}
        setSorting={setSorting}
        setFilters={handleFilters}
        refresh={() => fetchUsers()}
        topActions={[
          {
            label: getAddUserLabel(),
            icon: "add_line",
            do: () => setIsCreationOpen(true),
          },
        ]}
        rowActions={(row) =>
          listEditableRoles(user).includes(row.role)
            ? [
                {
                  label: t("details"),
                  icon: "eye_line",
                  do: () => navigate(`/admin/users/${row.id}`),
                },
                {
                  label: t("edit"),
                  icon: "pencil_line",
                  do: () => navigate(`/admin/users/${row.id}?edit`),
                },
              ]
            : []
        }
      />
      <CreationForm
        title={getAddUserLabel()}
        fields={fields(t, agencies, caisses)}
        isOpen={isCreationOpen}
        onClose={() => setIsCreationOpen(false)}
        onSubmit={(value) => handleSubmit(value)}
      />
    </>
  );
}
